/* eslint-disable react/no-array-index-key */

import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import RoleTitleManualPicker from "./RoleTitleManualPicker";
import api from "../../../api";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { RoleAndTitlesSet } from "../../../types/campaignTypes";
import { getCookieStateFromAdLibIds, getRoleTitleLabelsFromAdLibIds } from "./roleTitleSetUtils";
import {
  getRoleTitleList,
  setCampaignCookies,
  setSelectedRoleTitleSetId,
} from "../../../redux/reducers/campaignToCreate";
import { useRoleTitleSelection } from "./useRoleTitleSelection";

type Props = {
  panelMode?: boolean;
};

type TabKey = "set" | "manual";

const PanelRoot = styled("div")({ display: "flex", flexDirection: "column", gap: 12 });
const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));
const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });
const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({ background: theme.palette.brand.gray50 }));
const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));
const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));
const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));
const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": { padding: "0 !important", borderBottom: `1px solid ${theme.palette.brand.gray200}` },
}));
const CountCell = styled("span")({ display: "inline-flex", alignItems: "center", gap: 4 });
const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));
const Radio = styled("div")(({ theme }: { theme: Theme }) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `2px solid ${theme.palette.brand.gray300}`,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&[data-selected='true']": { borderColor: theme.palette.brand.coral },
  "& span": { width: 10, height: 10, borderRadius: "50%", background: theme.palette.brand.coral },
}));
const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "32px 20px",
  textAlign: "center" as const,
  fontSize: 14,
  color: theme.palette.brand.gray500,
  "& a": { color: theme.palette.brand.coral, textDecoration: "none", fontWeight: 500 },
  "& a:hover": { textDecoration: "underline" },
}));
const LoadingState = styled("div")({ padding: "32px", display: "flex", justifyContent: "center" });
const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "16px 0",
}));
const TagsWrapper = styled("div")({ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 16px 12px" });
const RoleTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const TabBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: theme.palette.brand.gray100,
  alignSelf: "flex-start",
}));
const TabButton = styled("button")(({ theme }: { theme: Theme }) => ({
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: theme.palette.brand.gray500,
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 16px",
  borderRadius: 8,
  transition: "all 0.15s ease",
  "&[data-active='true']": {
    background: theme.palette.brand.white,
    color: theme.palette.brand.gray900,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
}));

const RoleTitleSetSelectorPanel: FC<Props> = ({ panelMode = false }) => {
  const dispatch = useDispatch();
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const selectedRoleTitleSetId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedRoleTitleSetId,
  );
  const roleTitleList = useSelector((state: ReduxApplicationState) =>
    state.campaignToCreate.roleTitleList.filter((g) => g.roleType !== "Companies"),
  );
  const isRoleTitlePending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const campaignCookieSets = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignCookieSets);
  const brandgenAdvertiserId = selectedAdvertiser?.brandgenAdvertiserId ?? null;
  const [sets, setSets] = useState<RoleAndTitlesSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<TabKey>("set");

  const [globalSearch, setGlobalSearch] = useState("");

  // The "Choose manually" tab. campaignCookieSets in Redux is the single source of
  // truth for the final selection; the hook derives checkbox state from it and
  // dispatches edits back via onChange. Seeded once from any existing hand-picked
  // selection (returning to a campaign built manually rather than from a set).
  const selection = useRoleTitleSelection(
    () => (!selectedRoleTitleSetId && campaignCookieSets ? campaignCookieSets : {}),
    {
      onChange: (next) => {
        // Editing manually replaces any selected set (mutually exclusive modes).
        dispatch(setSelectedRoleTitleSetId(null));
        dispatch(setCampaignCookies(next));
      },
    },
  );

  useEffect(() => {
    if (roleTitleList.length === 0) dispatch(getRoleTitleList());
  }, [dispatch, roleTitleList.length]);

  useEffect(() => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    const fetchSets = async () => {
      try {
        const { payload } = await api.getRoleAndTitlesSets(brandgenAdvertiserId);
        setSets(Array.isArray(payload) ? payload : []);
      } catch {
        setSets([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSets();
  }, [brandgenAdvertiserId]);

  const handleSelect = (set: RoleAndTitlesSet) => {
    if (selectedRoleTitleSetId === set.id) {
      dispatch(setSelectedRoleTitleSetId(null));
      dispatch(setCampaignCookies({}));
      return;
    }
    // Picking a set replaces any manual selection (mutually exclusive modes).
    // reset() intentionally does not fire onChange — we dispatch the set's cookies below.
    selection.reset({});
    dispatch(setSelectedRoleTitleSetId(set.id));
    dispatch(setCampaignCookies(getCookieStateFromAdLibIds(roleTitleList, set.rolesAndTitlesAdLibIds)));
  };

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!selectedAdvertiser)
    return <AdvertiserPrompt>Please select an advertiser to load role & title sets.</AdvertiserPrompt>;

  if (isLoading || isRoleTitlePending || roleTitleList.length === 0) {
    return (
      <LoadingState>
        <CircularProgress size={28} />
      </LoadingState>
    );
  }

  return (
    <PanelRoot>
      {panelMode && (
        <ManageLink>
          To create or manage role & title sets, go to{" "}
          <a href="/roles-titles" target="_blank" rel="noopener noreferrer">
            Roles & Titles
          </a>
          .
        </ManageLink>
      )}
      <TabBar>
        <TabButton type="button" data-active={String(activeTab === "set")} onClick={() => setActiveTab("set")}>
          Pick a set
        </TabButton>
        <TabButton type="button" data-active={String(activeTab === "manual")} onClick={() => setActiveTab("manual")}>
          Choose manually
        </TabButton>
      </TabBar>
      {activeTab === "manual" ? (
        <RoleTitleManualPicker
          masonry
          checkBoxes={selection.checkBoxes}
          globalSearch={globalSearch}
          selectedCount={selection.selectedCount}
          onSearchChange={setGlobalSearch}
          onClearAll={selection.clearAll}
          handleChange={selection.handleChange}
          handleSelectAll={selection.toggleGroup}
        />
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>
            No role & title sets found for <strong>{selectedAdvertiser.name}</strong>.{" "}
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th style={{ width: 60 }} />
                <Th>Name</Th>
                <Th>Roles & Titles</Th>
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const isSelected = selectedRoleTitleSetId === set.id;
                const roleNames = getRoleTitleLabelsFromAdLibIds(roleTitleList, set.rolesAndTitlesAdLibIds);
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(set.id)) next.delete(set.id);
                          else next.add(set.id);
                          return next;
                        })
                      }
                    >
                      <Td
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(set);
                        }}
                      >
                        <Radio data-selected={String(isSelected)}>{isSelected && <span />}</Radio>
                      </Td>
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {roleNames.length.toLocaleString()}
                        </CountCell>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={3}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <TagsWrapper>
                            {roleNames.map((roleName, index) => (
                              <RoleTag key={`${roleName}-${index}`}>{roleName}</RoleTag>
                            ))}
                          </TagsWrapper>
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}
    </PanelRoot>
  );
};

export default RoleTitleSetSelectorPanel;
